import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { getCurrentUser } from '../services/authService';

function AccountSettingsPage() {
  const user = getCurrentUser();

  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('');

  // Section A: Preferences
  const [displayName, setDisplayName] = useState('');
  const [notifEmail, setNotifEmail] = useState(true);
  const [loadedPrefs, setLoadedPrefs] = useState(null);
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefStatus, setPrefStatus] = useState('');
  const [prefMessage, setPrefMessage] = useState('');

  // Section B: Auto-reply
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplyThreshold, setAutoReplyThreshold] = useState(2);
  const [autoReplySaving, setAutoReplySaving] = useState(false);
  const [autoReplySuccess, setAutoReplySuccess] = useState('');

  // Section C: Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwStatus, setPwStatus] = useState('');
  const [pwMessage, setPwMessage] = useState('');

  // Section D: Support Code — PROMPT #310. Same derivation as mobile's
  // Profile screen (mobile/lib/features/profile/utils/version_support_line.dart):
  // the raw Firebase Auth uid, labeled "Support Code" rather than "UID" so it
  // reads as an innocuous reference code, not an internal identifier.
  const [supportCodeCopied, setSupportCodeCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, 'users', user.uid)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setRole(data.role || data.profile?.role || '');
        const dn = data.displayName ?? '';
        const ne = data.preferences?.notifications?.email ?? true;
        setDisplayName(dn);
        setNotifEmail(ne);
        setLoadedPrefs({ displayName: dn, notifEmail: ne });
        setAutoReplyEnabled(data.autoReply?.enabled ?? false);
        setAutoReplyThreshold(data.autoReply?.thresholdHours ?? 2);
      }
      setLoading(false);
    });
  }, [user]);

  const handleSavePreferences = async () => {
    setPrefSaving(true);
    setPrefStatus('');
    setPrefMessage('');
    try {
      const updates = {};
      if (displayName.trim() !== (loadedPrefs?.displayName ?? '')) updates.displayName = displayName.trim();
      if (notifEmail !== (loadedPrefs?.notifEmail ?? true)) updates['preferences.notifications.email'] = notifEmail;
      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(db, 'users', user.uid), updates);
        setLoadedPrefs({ displayName: displayName.trim(), notifEmail });
      }
      setPrefStatus('success');
      setPrefMessage('Preferences saved.');
      setTimeout(() => { setPrefStatus(''); setPrefMessage(''); }, 2000);
    } catch {
      setPrefStatus('error');
      setPrefMessage('Failed to save. Please try again.');
    } finally {
      setPrefSaving(false);
    }
  };

  const handleAutoReplySave = async () => {
    setAutoReplySaving(true);
    setAutoReplySuccess('');
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'autoReply.enabled': autoReplyEnabled,
        'autoReply.thresholdHours': autoReplyThreshold,
      });
      setAutoReplySuccess('Settings saved.');
      setTimeout(() => setAutoReplySuccess(''), 3000);
    } catch (err) {
      console.error('Failed to save auto-reply settings:', err);
    } finally {
      setAutoReplySaving(false);
    }
  };

  const handleCopySupportCode = () => {
    navigator.clipboard.writeText(user.uid).then(() => {
      setSupportCodeCopied(true);
      setTimeout(() => setSupportCodeCopied(false), 2000);
    });
  };

  const handlePasswordUpdate = async () => {
    if (newPassword !== confirmPassword) {
      setPwStatus('error');
      setPwMessage('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPwStatus('error');
      setPwMessage('New password must be at least 8 characters.');
      return;
    }
    setPwSaving(true);
    setPwStatus('');
    setPwMessage('');
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPwStatus('success');
      setPwMessage('Password updated successfully.');
      setTimeout(() => { setPwStatus(''); setPwMessage(''); }, 3000);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setPwStatus('error');
        setPwMessage('Current password is incorrect.');
      } else {
        setPwStatus('error');
        setPwMessage('Failed to update password. Please try again.');
      }
    } finally {
      setPwSaving(false);
    }
  };

  const fieldStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '15px',
    boxSizing: 'border-box',
    color: '#1a202c',
    backgroundColor: 'white',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.4rem',
    fontWeight: '500',
    fontSize: '14px',
    color: '#374151',
  };

  const cardStyle = {
    backgroundColor: '#F8FAFC',
    borderRadius: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    padding: '2rem',
    marginBottom: '1.5rem',
  };

  const headingStyle = {
    fontSize: '17px',
    fontWeight: '500',
    marginTop: 0,
    marginBottom: '1.5rem',
    color: '#111827',
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem' }}>
        <div style={{ color: '#6b7280' }}>Loading...</div>
      </div>
    );
  }

  if (role !== 'employer') {
    return (
      <div style={{ padding: '2rem' }}>
        <p style={{ color: '#6b7280' }}>This page is for employers.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '640px' }}>

      {/* Section A: Preferences */}
      <div style={cardStyle}>
        <h2 style={headingStyle}>Preferences</h2>

        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Display Name</label>
          <input
            type="text"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            style={fieldStyle}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={labelStyle}>Notifications</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              onClick={() => setNotifEmail(!notifEmail)}
              style={{
                width: '44px',
                height: '24px',
                backgroundColor: notifEmail ? '#E8590C' : '#d1d5db',
                borderRadius: '12px',
                cursor: 'pointer',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <div style={{
                position: 'absolute',
                top: '2px',
                left: notifEmail ? '22px' : '2px',
                width: '20px',
                height: '20px',
                backgroundColor: 'white',
                borderRadius: '50%',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
              }} />
            </div>
            <label
              onClick={() => setNotifEmail(!notifEmail)}
              style={{ fontSize: '15px', fontWeight: '500', color: '#374151', cursor: 'pointer' }}
            >
              Email notifications
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={handleSavePreferences}
            disabled={prefSaving}
            style={{
              width: '160px',
              backgroundColor: '#E8590C',
              color: 'white',
              padding: '0.65rem',
              borderRadius: '6px',
              border: 'none',
              fontSize: '15px',
              fontWeight: '500',
              cursor: prefSaving ? 'not-allowed' : 'pointer',
              opacity: prefSaving ? 0.6 : 1,
            }}
          >
            {prefSaving ? 'Saving...' : 'Save Preferences'}
          </button>
          {prefStatus === 'success' && (
            <span style={{ color: '#065f46', fontSize: '14px', fontWeight: '500' }}>{prefMessage}</span>
          )}
          {prefStatus === 'error' && (
            <span style={{ color: '#991b1b', fontSize: '14px' }}>{prefMessage}</span>
          )}
        </div>
      </div>

      {/* Section B: Auto-Reply Settings */}
      <div style={cardStyle}>
        <h2 style={headingStyle}>Auto-Reply Settings</h2>
        <p style={{ fontSize: '14px', color: '#6b7280', marginTop: 0, marginBottom: '1.5rem', lineHeight: '1.5' }}>
          When enabled, WorkrMatch AI will send a holding reply on your behalf when a worker's message goes unanswered for the selected time. All auto-replies are clearly marked as automated.
        </p>

        {autoReplySuccess && (
          <div style={{ backgroundColor: '#d1fae5', color: '#065f46', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '14px' }}>
            {autoReplySuccess}
          </div>
        )}

        {/* Enable toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <div
            onClick={() => setAutoReplyEnabled(!autoReplyEnabled)}
            style={{
              width: '44px',
              height: '24px',
              backgroundColor: autoReplyEnabled ? '#E8590C' : '#d1d5db',
              borderRadius: '12px',
              cursor: 'pointer',
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <div style={{
              position: 'absolute',
              top: '2px',
              left: autoReplyEnabled ? '22px' : '2px',
              width: '20px',
              height: '20px',
              backgroundColor: 'white',
              borderRadius: '50%',
              boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            }} />
          </div>
          <label
            onClick={() => setAutoReplyEnabled(!autoReplyEnabled)}
            style={{ fontSize: '15px', fontWeight: '500', color: '#374151', cursor: 'pointer' }}
          >
            Enable AI auto-reply
          </label>
        </div>

        {/* Threshold selector */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={labelStyle}>Reply after</label>
          <select
            value={autoReplyThreshold}
            onChange={e => setAutoReplyThreshold(Number(e.target.value))}
            disabled={!autoReplyEnabled}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '15px',
              color: autoReplyEnabled ? '#374151' : '#9ca3af',
              backgroundColor: autoReplyEnabled ? 'white' : '#f9fafb',
              cursor: autoReplyEnabled ? 'pointer' : 'not-allowed',
            }}
          >
            <option value={1}>1 hour</option>
            <option value={2}>2 hours</option>
            <option value={4}>4 hours</option>
            <option value={8}>8 hours</option>
            <option value={24}>24 hours</option>
          </select>
        </div>

        <button
          onClick={handleAutoReplySave}
          disabled={autoReplySaving}
          style={{ padding: '8px 20px', backgroundColor: '#E8590C', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: autoReplySaving ? 'not-allowed' : 'pointer', display: 'block', marginLeft: 'auto', marginTop: '16px', opacity: autoReplySaving ? 0.6 : 1 }}
        >
          {autoReplySaving ? 'Saving...' : 'Save Auto-reply'}
        </button>
      </div>

      {/* Section C: Change Password */}
      <div style={cardStyle}>
        <h2 style={headingStyle}>Change Password</h2>

        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Current Password</label>
          <input
            type="password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            style={fieldStyle}
          />
        </div>

        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>New Password</label>
          <input
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            autoComplete="new-password"
            style={fieldStyle}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={labelStyle}>Confirm New Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            style={fieldStyle}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={handlePasswordUpdate}
            disabled={pwSaving}
            style={{
              width: '160px',
              backgroundColor: '#E8590C',
              color: 'white',
              padding: '0.65rem',
              borderRadius: '6px',
              border: 'none',
              fontSize: '15px',
              fontWeight: '500',
              cursor: pwSaving ? 'not-allowed' : 'pointer',
              opacity: pwSaving ? 0.6 : 1,
            }}
          >
            {pwSaving ? 'Updating...' : 'Update Password'}
          </button>
          {pwStatus === 'success' && (
            <span style={{ color: '#065f46', fontSize: '14px', fontWeight: '500' }}>{pwMessage}</span>
          )}
          {pwStatus === 'error' && (
            <span style={{ color: '#991b1b', fontSize: '14px' }}>{pwMessage}</span>
          )}
        </div>
      </div>

      {/* Section D: Support Code */}
      <div style={cardStyle}>
        <h2 style={headingStyle}>Support Code</h2>
        <p style={{ fontSize: '14px', color: '#6b7280', marginTop: 0, marginBottom: '1rem', lineHeight: '1.5' }}>
          If you contact support, they may ask for this code to look up your account.
        </p>
        <div
          onClick={handleCopySupportCode}
          title="Click to copy"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.5rem 0.75rem',
            backgroundColor: '#F1F5F9',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontFamily: 'monospace',
            fontSize: '13px',
            color: '#374151',
          }}
        >
          <span>Support Code: {user.uid}</span>
        </div>
        {supportCodeCopied && (
          <div style={{ marginTop: '0.5rem', color: '#065f46', fontSize: '13px', fontWeight: '500' }}>
            Copied to clipboard.
          </div>
        )}
      </div>

      {/* Section E: Legal (Prompt #368) — real, connected pro.workrmatch.app
          domain, same one terms_acceptance_screen.dart's mobile link uses
          (Prompt #362) — not the dead bare apex. */}
      <div style={cardStyle}>
        <h2 style={headingStyle}>Legal</h2>
        <div style={{ display: 'flex', gap: '1.5rem' }}>
          <a
            href="https://pro.workrmatch.app/terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#E8590C', fontSize: '14px', fontWeight: '500', textDecoration: 'none' }}
          >
            Terms of Service
          </a>
          <a
            href="https://pro.workrmatch.app/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#E8590C', fontSize: '14px', fontWeight: '500', textDecoration: 'none' }}
          >
            Privacy Policy
          </a>
        </div>
      </div>

      {/* PHASE 2 — Subscription section */}
      {/* Show current planTier, slot usage, billingAnchor date */}
      {/* Upgrade CTA for Starter and Growth employers */}
      {/* Add here when plan tier feature ships */}
    </div>
  );
}

export default AccountSettingsPage;
